import { useState } from "react";
import { Share2, Copy, Check, Mail } from "lucide-react";
import { toast } from "sonner";
import {
  RetroDialog,
  RetroDialogHeader,
  RetroDialogBody,
} from "@/components/ui/retro-dialog";

interface ShareEpisodeProps {
  episodeSlug: string;
  episodeTitle: string;
  podcastName?: string;
  /** Render variant */
  variant?: "icon" | "button" | "comic" | "minimal";
  className?: string;
}

const BASE_URL = "https://best-pod-pick.lovable.app";
const OG_BASE = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/episode-og`;

export default function ShareEpisode({
  episodeSlug,
  episodeTitle,
  podcastName,
  variant = "icon",
  className = "",
}: ShareEpisodeProps) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [email, setEmail] = useState("");

  const ogUrl = `${OG_BASE}?slug=${encodeURIComponent(episodeSlug)}`;
  const subject = encodeURIComponent(`Check out: ${episodeTitle}`);
  const body = encodeURIComponent(
    `I found this great podcast episode on AskThePods:\n\n"${episodeTitle}"${podcastName ? ` — ${podcastName}` : ""}\n\n${ogUrl}`
  );

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(ogUrl);
      setCopied(true);
      toast.success("Link copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Failed to copy");
    }
  };

  const handleEmailSend = () => {
    if (!email.trim()) return;
    window.open(`mailto:${email}?subject=${subject}&body=${body}`, "_blank");
    setEmail("");
    toast.success("Opening email client…");
  };

  const handleNativeShare = async () => {
    try {
      await navigator.share({
        title: episodeTitle,
        text: `${episodeTitle}${podcastName ? ` — ${podcastName}` : ""}`,
        url: ogUrl,
      });
    } catch (err: unknown) {
      // User cancelled — ignore AbortError
      if (err instanceof Error && err.name !== "AbortError") {
        // Fallback to dialog
        setOpen(true);
      }
    }
  };

  const handleTriggerClick = () => {
    if (navigator.share) {
      handleNativeShare();
    } else {
      setOpen(true);
    }
  };

  const trigger =
    variant === "comic" ? (
      <button
        onClick={handleTriggerClick}
        className={`comic-play-btn relative overflow-hidden flex items-center gap-1 text-[10px] sm:text-xs font-mono font-bold px-2 sm:px-3 py-1 border-[2px] rounded-md bg-[hsl(195,85%,90%)] text-[hsl(195,80%,20%)] border-[hsl(195,80%,28%)] shadow-[3px_3px_0px_0px_hsl(var(--foreground))] hover:shadow-[1px_1px_0px_0px_hsl(var(--foreground))] hover:translate-x-[2px] hover:translate-y-[2px] transition-all duration-150 transform hover:scale-[1.03] hover:rotate-[-0.5deg] active:scale-95 ${className}`}
        title="Share episode"
      >
        <Share2 className="w-3 h-3 relative z-10" />
        <span className="relative z-10">Share</span>
      </button>
    ) : variant === "button" ? (
      <button
        onClick={handleTriggerClick}
        className={`btn-comic btn-comic-shift gap-1.5 text-xs px-4 py-2 ${className}`}
        title="Share episode"
      >
        <Share2 className="w-3.5 h-3.5 relative z-10" />
        <span className="relative z-10">Share</span>
      </button>
    ) : variant === "minimal" ? (
      <button
        onClick={handleTriggerClick}
        className={`p-1 text-muted-foreground hover:text-foreground transition-colors ${className}`}
        title="Share episode"
        aria-label="Share episode"
      >
        <Share2 className="w-4 h-4" />
      </button>
    ) : (
      <button
        onClick={handleTriggerClick}
        className={`btn-comic btn-comic-shift text-xs p-2 ${className}`}
        title="Share episode"
        aria-label="Share episode"
      >
        <Share2 className="w-4 h-4 relative z-10" />
      </button>
    );

  return (
    <>
      {trigger}
      <RetroDialog open={open} onOpenChange={setOpen} size="md">
        <RetroDialogHeader title="Share Episode" />
        <RetroDialogBody>
          <p className="font-mono text-xs text-muted-foreground line-clamp-2 mb-4">
            {episodeTitle}
            {podcastName && <span className="text-foreground"> — {podcastName}</span>}
          </p>

          {/* Copy link */}
          <div className="mb-4">
            <label className="font-mono text-[10px] text-muted-foreground uppercase tracking-wider block mb-1.5">
              Share Link <span className="normal-case">(rich previews on social media)</span>
            </label>
            <div className="flex gap-2">
              <input
                readOnly
                value={ogUrl}
                className="input-retro flex-1 truncate text-xs"
                onFocus={(e) => e.target.select()}
              />
              <button
                onClick={handleCopy}
                className="btn-comic-primary btn-comic-sm flex-shrink-0"
              >
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
          </div>

          {/* Email share */}
          <div>
            <label className="font-mono text-[10px] text-muted-foreground uppercase tracking-wider block mb-1.5">
              Share via Email
            </label>
            <div className="flex gap-2">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="friend@example.com"
                className="input-retro flex-1 text-xs"
                onKeyDown={(e) => e.key === "Enter" && handleEmailSend()}
              />
              <button
                onClick={handleEmailSend}
                disabled={!email.trim()}
                className="btn-comic-primary btn-comic-sm flex-shrink-0 disabled:opacity-50"
              >
                <Mail className="w-3.5 h-3.5" />
                Send
              </button>
            </div>
          </div>
        </RetroDialogBody>
      </RetroDialog>
    </>
  );
}